import {
  COMBINE_RGB_ALPHA_R,
  FILTER_AREA,
  FILTER_BILINEAR,
  GpuContext,
  GpuImage,
  UNPACK_MASK,
  chokeSpread,
  combine,
  finishMatte,
  guidedFilter,
  normalizeMatte,
  packTensor,
  readTexture,
  removeIslands,
  resample,
  resampleTo,
  tensorByteLength,
  tensorDims,
  unpackTensor,
  type TensorSpec,
} from '@design-ai/gpu-core';
import { OnnxModel, TensorBufferCache, type ModelIO } from '@design-ai/inference';
import type { MatteSettings } from './settings.js';

/**
 * Runs a background-removal network and turns its output into a usable matte.
 *
 * Split deliberately into two phases:
 *
 *   `computeMatte` - one network pass, hundreds of milliseconds, run once per image
 *   `refine`       - pure GPU post-processing, milliseconds, run on every slider move
 *
 * Everything a user can tune lives in the second phase, so the workbench stays
 * interactive without ever re-running the model.
 */
export class MattingStage {
  readonly #buffers: TensorBufferCache;

  constructor(
    private readonly ctx: GpuContext,
    private readonly model: OnnxModel,
    private readonly io: ModelIO,
  ) {
    this.#buffers = new TensorBufferCache(ctx);
  }

  /** Network input edge; RMBG-1.4 is fixed at 1024. */
  get inputSize(): number {
    return typeof this.io.size === 'number' ? this.io.size : 1024;
  }

  /**
   * Produces the raw matte at source resolution (`r32float`).
   *
   * The source is squashed to the network's square input rather than
   * letterboxed: that is how RMBG-1.4 was trained, and preserving the aspect
   * ratio instead measurably degrades the matte on wide crops.
   */
  async computeMatte(source: GpuImage, normalize = true): Promise<GpuImage> {
    const size = this.inputSize;
    const spec: TensorSpec = {
      width: size,
      height: size,
      channels: this.io.channels,
      dtype: this.io.dtype,
      layout: this.io.layout,
    };

    const input = resampleTo(this.ctx, source, size, size, { filter: FILTER_AREA });
    const buffer = this.#buffers.get(tensorByteLength(spec), 'matting-input');
    packTensor(this.ctx, input, buffer, spec, { mean: this.io.mean, std: this.io.std });
    // The pack must have executed before ORT reads the buffer.
    await this.ctx.sync();
    input.release();

    const output = await this.model.runGpu(this.ctx, buffer, tensorDims(spec), this.io.dtype);
    const outputSpec = describeOutput(output.dims, size, output.dtype, this.io);

    const rawSmall = this.ctx.image(outputSpec.width, outputSpec.height, 'r32float', 'matte-raw');
    unpackTensor(this.ctx, output.buffer, rawSmall, outputSpec, { mode: UNPACK_MASK });
    this.ctx.flush();
    output.dispose();

    const normalized = this.ctx.image(rawSmall.width, rawSmall.height, 'r32float', 'matte-norm');
    normalizeMatte(this.ctx, rawSmall, normalized, normalize);
    rawSmall.release();

    // Back to source resolution. The guided filter in `refine` is what actually
    // recovers the detail this upsample cannot.
    const full = this.ctx.image(source.width, source.height, 'r32float', 'matte-full');
    resample(this.ctx, normalized, full, { filter: FILTER_BILINEAR });
    normalized.release();
    this.ctx.flush();
    return full;
  }

  dispose(): void {
    this.#buffers.destroy();
  }
}

/**
 * Post-processing chain. Cheap enough to re-run on every parameter change.
 *
 * Order matters: refine the edge before thresholding it (the guided filter needs
 * the soft transition band to work with), shrink before removing islands (so a
 * choked-away sliver does not still count as a component), and decontaminate
 * last, once alpha is final.
 */
export function refineMatte(
  ctx: GpuContext,
  source: GpuImage,
  rawMatte: GpuImage,
  paint: GpuImage | null,
  settings: MatteSettings,
  out: GpuImage,
): void {
  const width = source.width;
  const height = source.height;

  const refined = ctx.image(width, height, 'r32float', 'matte-refined');
  if (settings.guidedRadius > 0) {
    guidedFilter(ctx, source, rawMatte, refined, {
      radius: settings.guidedRadius,
      eps: settings.guidedEps,
      subsample: settings.guidedSubsample,
    });
  } else {
    combine(ctx, source, rawMatte, refined, COMBINE_RGB_ALPHA_R);
  }

  let alpha = refined;
  if (settings.choke !== 0) {
    const choked = ctx.image(width, height, 'r32float', 'matte-choked');
    chokeSpread(ctx, alpha, choked, settings.choke);
    alpha.release();
    alpha = choked;
  }

  if (settings.minIslandFraction > 0) {
    const cleaned = ctx.image(width, height, 'r32float', 'matte-islands');
    removeIslands(ctx, alpha, cleaned, {
      minAreaFraction: settings.minIslandFraction,
      threshold: Math.max(0.05, settings.low),
    });
    alpha.release();
    alpha = cleaned;
  }

  finishMatte(ctx, source, alpha, out, {
    low: settings.low,
    high: settings.high,
    gamma: settings.gamma,
    gain: settings.gain,
    paint,
    paintStrength: settings.paintStrength,
    despill: settings.despill,
    background: settings.background ?? [1, 1, 1],
    premultiply: false,
  });
  alpha.release();
}

/**
 * Estimates the background colour from pixels the matte considers transparent.
 *
 * Decontamination needs to know what colour is bleeding into the artwork's edge.
 * On a POD mockup that is the garment, and it is neither white nor constant
 * across the crop, so guessing white leaves a visible fringe. Two 64x64
 * readbacks are cheap and only happen when a new image is loaded.
 */
export async function estimateBackground(
  ctx: GpuContext,
  source: GpuImage,
  matte: GpuImage,
): Promise<[number, number, number]> {
  const size = 64;
  const smallSource = ctx.image(size, size, 'rgba8unorm', 'bg-probe-color');
  resample(ctx, source, smallSource, { filter: FILTER_AREA });
  const smallMatte = ctx.image(size, size, 'rgba8unorm', 'bg-probe-alpha');
  resample(ctx, matte, smallMatte, { filter: FILTER_AREA });

  const [colorBytes, matteBytes] = await Promise.all([
    readTexture(ctx, smallSource),
    readTexture(ctx, smallMatte),
  ]);
  smallSource.release();
  smallMatte.release();

  let r = 0;
  let g = 0;
  let b = 0;
  let weight = 0;
  for (let i = 0; i < size * size; i++) {
    // `resample` writes the r32float matte's value into the red channel.
    const coverage = matteBytes[i * 4]! / 255;
    if (coverage > 0.15) continue;
    const w = 1 - coverage;
    r += (colorBytes[i * 4]! / 255) * w;
    g += (colorBytes[i * 4 + 1]! / 255) * w;
    b += (colorBytes[i * 4 + 2]! / 255) * w;
    weight += w;
  }
  if (weight < 1e-3) return [1, 1, 1];
  return [r / weight, g / weight, b / weight];
}

/**
 * Reads the model's output shape.
 *
 * Salient-object networks in this family return `[1, 1, H, W]`, but some export
 * a list of side outputs or drop the channel axis, so the shape is interpreted
 * rather than assumed.
 */
function describeOutput(
  dims: readonly number[],
  fallbackSize: number,
  dtype: TensorSpec['dtype'],
  io: ModelIO,
): TensorSpec {
  const width = dims.length >= 2 ? dims[dims.length - 1]! : fallbackSize;
  const height = dims.length >= 2 ? dims[dims.length - 2]! : fallbackSize;
  return {
    width,
    height,
    channels: io.outputChannels,
    dtype,
    layout: io.layout,
  };
}
